import { useCallback, useEffect, useMemo, useState } from "react";
import CalendarWeb from "./CalendarWeb";
import moment from "moment";
import CustomSelect from "./CustomSelect";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { debounce, startCase } from "lodash";
import { formatWithCommas, toFixedWithoutRounding } from "../../Utils/Toast";

const TABS = [
  { key: "transaction", label: "Transaction Records" },
  { key: "deposit", label: "Deposit Records" },
  { key: "withdraw", label: "Withdrawal Records" },
];

const HEADERS = {
  transaction: [
    "Transaction type",
    "Transaction time",
    "Transaction amount",
    "Current balance",
    "Order reference number",
    "Content",
  ],
  deposit: [
    "Bank",
    "Transaction time",
    "Requested amount",
    "Received amount",
    "Status",
    "Promotions",
    "Remark",
  ],
  withdraw: [
    "Bank",
    "Transaction time",
    "Requested amount",
    "Received amount",
    "Status",
    "Remark",
  ],
};

const TYPE_OPTIONS = ["All", "Deposit", "Withdrawal", "Bonus", "Transfer", "Adjustment"];

const ENDPOINTS = {
  deposit: apiPath.depositHistory,
  withdraw: apiPath.withDrawHistory,
  transaction: apiPath.accountStatement,
};

const FORMAT = "YYYY-MM-DD HH:mm:ss";

const statusLabel = (res) => {
  if (res?.transferedProcess === "process") return "Processing";
  if (res?.status === "completed") return "Approve";
  if (res?.transferedProcess === "requested") return "Requested";
  return "Decline";
};

const money = (value, decimals) => {
  const num = Number(value || 0);
  if (!decimals) return formatWithCommas(Math.round(num));
  return toFixedWithoutRounding(num);
};

export default function AccountRecord() {
  const [select, setSelect] = useState("transaction");
  const [showCalendar, setShowCalendar] = useState(false);
  const [loader, setLoader] = useState(false);
  const [listing, setListing] = useState([]);
  const [total, setTotal] = useState(0);
  const [decimals, setDecimals] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    fromPeriod: moment().startOf("day").format(FORMAT),
    toPeriod: moment().endOf("day").format(FORMAT),
    type: "All",
  });

  const from = moment(filter.fromPeriod);
  const to = moment(filter.toPeriod);
  const isToday = from.isSame(moment(), "day") && to.isSame(moment(), "day");
  const isYesterday =
    from.isSame(moment().subtract(1, "day"), "day") &&
    to.isSame(moment().subtract(1, "day"), "day");
  const isLast7Days =
    from.isSame(moment().subtract(6, "days"), "day") && to.isSame(moment(), "day");

  const setRange = (preset) => {
    if (preset === "today") {
      return {
        fromPeriod: moment().startOf("day").format(FORMAT),
        toPeriod: moment().endOf("day").format(FORMAT),
      };
    }
    if (preset === "yesterday") {
      return {
        fromPeriod: moment().subtract(1, "day").startOf("day").format(FORMAT),
        toPeriod: moment().subtract(1, "day").endOf("day").format(FORMAT),
      };
    }
    return {
      fromPeriod: moment().subtract(6, "days").startOf("day").format(FORMAT),
      toPeriod: moment().endOf("day").format(FORMAT),
    };
  };

  const getData = async (obj = filter, tab = select) => {
    const { status, data } = await apiGet(ENDPOINTS[tab], {
      fromPeriod: moment(obj.fromPeriod).format("YYYY-MM-DD"),
      toPeriod: moment(obj.toPeriod).format("YYYY-MM-DD"),
      page: obj.page,
      limit: obj.limit,
      status: obj.type,
    });
    if (status === 200 && data?.success) {
      if (tab === "transaction") {
        const rows = data?.results?.data || [];
        setListing(rows);
        setTotal(
          rows.reduce((sum, row) => sum + Number(row?.amount || 0), 0)
        );
      } else {
        const rows = data?.results?.length ? data.results : [];
        setListing(rows);
        setTotal(
          rows.reduce(
            (sum, row) => sum + Number(row?.amount || 0) + (row?.bonus > 0 ? Number(row.bonus) : 0),
            0
          )
        );
      }
    } else {
      setListing([]);
      setTotal(0);
    }
    setLoader(false);
  };

  const debouncedFetch = useCallback(
    debounce((obj, tab) => {
      getData(obj, tab);
    }, 400),
    []
  );

  useEffect(() => {
    setListing([]);
    setTotal(0);
    setLoader(true);
    debouncedFetch(filter, select);
  }, [filter, select]);

  const headers = HEADERS[select];
  const colCount = headers.length;

  const filteredListing = useMemo(() => {
    if (select !== "transaction" || filter.type === "All") return listing;
    const needle = filter.type.toLowerCase();
    return listing.filter((row) => {
      const type = String(row?.gameType || row?.betFaireType || row?.transactionType || row?.type || "").toLowerCase();
      return type.includes(needle);
    });
  }, [listing, filter.type, select]);

  const applyPreset = (preset) => {
    const range = setRange(preset);
    setFilter((prev) => ({ ...prev, ...range, page: 1 }));
  };

  const renderRow = (res, index) => {
    if (select === "deposit") {
      const cells = [
        startCase(res?.bank_name) || startCase(res?.bank) || "—",
        moment(res?.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        money(res?.amount, decimals),
        money(Number(res?.amount || 0) + (res?.bonus > 0 ? Number(res.bonus) : 0), decimals),
        statusLabel(res),
        res?.promotionCode || "—",
        res?.remark || "—",
      ];
      return (
        <div className="reference-account-record-tr" key={res?._id || index}>
          {cells.map((value, cell) => <div key={headers[cell]} data-label={headers[cell]}>{value}</div>)}
        </div>
      );
    }
    if (select === "withdraw") {
      const cells = [
        startCase(res?.bank_name) || startCase(res?.bank) || "—",
        moment(res?.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        money(res?.amount, decimals),
        money(Number(res?.amount || 0) + (res?.bonus > 0 ? Number(res.bonus) : 0), decimals),
        statusLabel(res),
        res?.remark || "—",
      ];
      return (
        <div className="reference-account-record-tr" key={res?._id || index}>
          {cells.map((value, cell) => <div key={headers[cell]} data-label={headers[cell]}>{value}</div>)}
        </div>
      );
    }

    const amount = Number(res?.amount || 0);
    const signed =
      res?.transactionType === "credit"
        ? money(amount, decimals)
        : `(${money(Math.abs(amount), decimals)})`;

    const cells = [
      { value: startCase(res?.gameType || res?.betFaireType || res?.transactionType || res?.type) || "—" },
      { value: moment(res?.createdAt || res?.timeInserted).format("MM-DD HH:mm:ss") },
      { value: signed, className: res?.transactionType === "credit" ? "is-credit" : "is-debit" },
      { value: money(res?.balance ?? res?.currentBalance ?? res?.afterBalance, decimals) },
      { value: res?.orderId || res?.betId || res?.transactionId || res?._id || "—" },
      { value: res?.remark || res?.content || "—" },
    ];
    return (
      <div className="reference-account-record-tr" key={res?._id || index}>
        {cells.map((cell, index) => (
          <div key={headers[index]} className={cell.className} data-label={headers[index]}>{cell.value}</div>
        ))}
      </div>
    );
  };

  return (
    <div className={`reference-account-record reference-account-record-${select}`} id="mc_container">
      <div className="reference-account-record-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={select === tab.key ? "active" : undefined}
            onClick={() => setSelect(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="reference-account-record-filters">
        <label className={isToday ? "active" : undefined}>
          <input type="radio" name="account-record-date" checked={isToday} onChange={() => applyPreset("today")} />
          <span>Today</span>
        </label>
        <label className={isYesterday ? "active" : undefined}>
          <input type="radio" name="account-record-date" checked={isYesterday} onChange={() => applyPreset("yesterday")} />
          <span>Yesterday</span>
        </label>
        <label className={isLast7Days ? "active" : undefined}>
          <input type="radio" name="account-record-date" checked={isLast7Days} onChange={() => applyPreset("7days")} />
          <span>7-Days</span>
        </label>

        <button type="button" className="reference-account-record-date" onClick={() => setShowCalendar(true)}>
          {`${moment(filter.fromPeriod).format("MM/DD")}~${moment(filter.toPeriod).format("MM/DD")}`}
        </button>

        {select === "transaction" ? (
          <div className="reference-account-record-type">
            <span>Transaction type</span>
            <CustomSelect
              value={filter.type}
              option={TYPE_OPTIONS}
              onChange={(type) => setFilter((prev) => ({ ...prev, type, page: 1 }))}
            />
          </div>
        ) : null}

        <button
          type="button"
          className="reference-account-record-search"
          onClick={() => {
            setLoader(true);
            getData(filter, select);
          }}
        >
          Search
        </button>
      </div>

      {showCalendar ? (
        <CalendarWeb
          fromPeriod={filter.fromPeriod}
          toPeriod={filter.toPeriod}
          setFilter={setFilter}
          onCancel={() => setShowCalendar(false)}
        />
      ) : null}

      <div className="reference-account-record-table" style={{ "--cols": colCount }}>
        <div className="reference-account-record-head">
          {headers.map((label) => (
            <div key={label}>{label}</div>
          ))}
        </div>

        <div className="reference-account-record-body">
          {loader ? <div className="reference-account-record-loading" /> : null}
          {!loader && filteredListing.length === 0 ? (
            <div className="reference-account-record-empty">No matched data found</div>
          ) : (
            filteredListing.map(renderRow)
          )}
        </div>

        <div className="reference-account-record-foot">
          <span>Total</span>
          <strong>{total === 0 ? "0.00" : money(total, decimals)}</strong>
        </div>
      </div>

      <div className="reference-account-record-decimal">
        <span>Decimal point</span>
        <button
          type="button"
          className={decimals ? "on" : undefined}
          aria-label="Decimal point"
          aria-pressed={decimals}
          onClick={() => setDecimals((prev) => !prev)}
        >
          <i />
        </button>
      </div>
    </div>
  );
}
